//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';

import { SamplingCampaignRequest } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import {
  SamplingDeliveryMethod,
  SamplingOpportunitiesSortOrder,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
} from '@apis/campaign-requests/shared/Pagination';
import { SortDir } from '@apis/campaign-requests/shared/Sorting';
import { ChangePurchaseOptionConfirmation } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/ChangePurchaseOptionModal/ChangePurchaseOptionConfirmation';
import {
  ChangePurchaseOptionModalProvider,
  ChangePurchaseOptionModalStep,
} from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/ChangePurchaseOptionModal/ChangePurchaseOptionModalContext';
import { ChangePurchaseOptionModalTable } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/ChangePurchaseOptionModal/ChangePurchaseOptionModalTable';
import { ContentDestinationsStep } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/ContentDestinationModal';
import { PrepaidLocationModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/PrepaidLocationModal';
import { PrepaidRetailerSelectionModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/PrepaidRetailerSelectionModal';
import { CampaignModalContentSetup } from '@campaigns/components/campaigns-overview/create-campaign-modal/CampaignModalContentSetup';
import { ExpiredPurchaseOptionsTable } from '@campaigns/components/shared/prepaid-purchase-options/ExpiredPurchaseOptionsTable';
import { getSortParam } from '@campaigns/components/shared/prepaid-purchase-options/PrepaidPurchaseOptionsSortUtil';
import { usePrepaidPurchaseOptions } from '@campaigns/components/shared/purchase-options/usePrepaidPurchaseOptions';
import { usePrepaidPurchaseOptionSelectionChange } from '@campaigns/components/shared/purchase-options/usePurchaseOptionSelectionChange';
import { SEARCH_DEBOUNCE_DELAY } from '@campaigns/constants/TimeConstants';
import {
  CHANGE_PURCHASE_OPTION_HEADER_TEXT,
  CHANGE_PURCHASE_OPTION_MODAL_TITLE,
  MIN_PURCHASE_OPTION_SEARCH_LENGTH,
  PurchaseOptionsSortKey,
  UPDATE_DRAFT_CAMPAIGN_HEADER_TEXT,
  UPDATE_DRAFT_CAMPAIGN_MODAL_TITLE,
  UPDATE_EXPIRED_CHANGE_PURCHASE_OPTION_MODAL_TITLE,
  USER_ERROR_MESSAGES,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { SamplingCampaignRequestSectionName } from '@campaigns/constants/sampling/SamplingCampaignRequestSectionConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { getIsSamplingContentDestinationsEnabled } from '@campaigns/redux/selectors/getIsSamplingContentDestinationsEnabled';
import { isSingleRetailerPurchaseOption } from '@campaigns/utils/sampling/PrepaidCampaignUtils';
import {
  getFulfillmentOptionsFromPurchaseOptions,
  getMemberTasksFromPurchaseOptions,
  getPurchaseOptionProductTypes,
} from '@campaigns/utils/sampling/SamplingCampaignRequestUtils';
import { isPurchaseOptionExpiredOrZeroUnits } from '@campaigns/utils/sampling/SamplingPrepaidValidationUtils';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { Modal } from '@components/molecules/modal/Modal';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';

interface ChangePurchaseOptionModalProps {
  isLoading?: boolean;
  isModalOpen: boolean;
  onClose: () => void;
  shouldIncludeTertiaryButton?: boolean;
}

export const ChangePurchaseOptionModal = ({
  isLoading = false,
  isModalOpen,
  onClose,
  shouldIncludeTertiaryButton = false,
}: ChangePurchaseOptionModalProps) => {
  const mainFormMethods = useFormContext<SamplingCampaignRequest>();
  const localFormMethods = useForm<SamplingCampaignRequest>({
    defaultValues: mainFormMethods.getValues(),
    mode: 'onChange',
  });

  const {
    clearErrors,
    control,
    formState: { defaultValues, errors },
    getValues,
    reset: resetLocalForm,
    setError,
    setValue,
  } = localFormMethods;

  const { getValues: getMainValues, reset: resetMainForm } = mainFormMethods;

  useEffect(() => {
    if (isModalOpen) {
      resetLocalForm(getMainValues());
    }
  }, [getMainValues, isModalOpen, resetLocalForm]);

  const selectedOptions =
    useWatch({
      control,
      name: 'purchaseOption',
    }) || [];

  const savedPurchaseOptionsValues = useWatch({
    control: mainFormMethods.control,
    name: 'purchaseOption',
  });
  const type = useWatch({ control, name: 'type' });
  const communityType = useWatch({ control, name: 'communityType' });
  const productTypes = getPurchaseOptionProductTypes(type, communityType);

  const {
    isPending: isCheckingExpired,
    purchaseOptions: availableSavedOptions,
  } = usePrepaidPurchaseOptions({
    request: {
      excludeZeroBalance: false,
      ...(productTypes ? { productTypes } : {}),
    },
    selectedOptions: savedPurchaseOptionsValues,
  });

  const expiredSavedIds = new Set(
    availableSavedOptions
      .filter(isPurchaseOptionExpiredOrZeroUnits)
      .map((opt) => opt.opportunityId),
  );
  const activeSelectedOptions = selectedOptions.filter(
    (opt) => !expiredSavedIds.has(opt.opportunityId),
  );
  const selectedOptionIds = activeSelectedOptions.map(
    (opt) => opt.opportunityId,
  );
  const isCompatible = !errors.purchaseOption;

  const { handleSave, isSaving, samplingCampaignRequest } =
    useSamplingCampaign();

  const isLegacy = isUndefined(samplingCampaignRequest?.isNewPurchase);

  const isSamplingContentDestinationsEnabled = useSelector(
    getIsSamplingContentDestinationsEnabled,
  );
  const [step, setStep] = useState<ChangePurchaseOptionModalStep>(
    ChangePurchaseOptionModalStep.PURCHASE_OPTIONS,
  );
  const isSingleRetailerSelectionStep =
    step === ChangePurchaseOptionModalStep.SINGLE_RETAILER_SELECTION;
  const isContentDestinationsStep =
    step === ChangePurchaseOptionModalStep.CONTENT_DESTINATIONS;
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const isNewPurchase = localFormMethods.watch('isNewPurchase');

  const handleCreateNewPurchase = () => {
    setValue('purchaseOption', []);
    setValue('isNewPurchase', true);
    setStep(ChangePurchaseOptionModalStep.SETUP);
  };

  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NUMBER);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState<PurchaseOptionsSortKey>(
    PurchaseOptionsSortKey.EXPIRY_DATE,
  );
  const [sortDir, setSortDir] = useState<SortDir>(SortDir.ASC);

  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY);

  const {
    error,
    isPending,
    purchaseOptions,
    total: totalItemsResponse,
  } = usePrepaidPurchaseOptions({
    request: {
      excludeZeroBalance: true,
      ...(productTypes ? { productTypes } : {}),
      limit: itemsPerPage,
      offset: currentPage * itemsPerPage,
      searchProperty:
        debouncedSearchTerm.length >= MIN_PURCHASE_OPTION_SEARCH_LENGTH
          ? debouncedSearchTerm
          : undefined,
      sortBy: getSortParam(sortKey),
      sortOrder:
        sortDir === SortDir.ASC
          ? SamplingOpportunitiesSortOrder.ascending
          : SamplingOpportunitiesSortOrder.descending,
    },
    selectedOptions,
  });

  const totalItems = totalItemsResponse ?? 0;
  const resultsText = pluralize(totalItems, 'option');

  const handleSortChange = (key: PurchaseOptionsSortKey) => {
    if (key === sortKey) {
      setSortDir((prevSortDir) =>
        prevSortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC,
      );
    } else {
      setSortKey(key);
      setSortDir(SortDir.ASC);
    }
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  const handleSelectionChange = usePrepaidPurchaseOptionSelectionChange({
    filterForCompatibilityCheck: (opts) =>
      opts.filter((opt) => !expiredSavedIds.has(opt.opportunityId)),
    formActions: {
      clearPurchaseOptionError: () => clearErrors('purchaseOption'),
      setPurchaseOptionError: (message) =>
        setError('purchaseOption', { message }),
      setPurchaseOptionValue: (value, options) =>
        setValue('purchaseOption', value, options),
    },
    purchaseOptions,
    selectedOptions,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  const handleClose = () => {
    setStep(ChangePurchaseOptionModalStep.PURCHASE_OPTIONS);
    onClose();
  };

  const handleGoBackToCampaigns = () => {
    void navigateToState('campaigns.sampling');
  };

  const getConfirmationContext = () => {
    const { fulfillmentType, whoShipsProduct } =
      getValues('fulfillmentOptions') || {};

    const targetDeliveryMethod = activeSelectedOptions[0]?.deliveryMethod;

    const isSourceShippedByBV =
      fulfillmentType === 'shipped' && whoShipsProduct === 'bazaarvoice';
    const isSourceDeals = fulfillmentType === 'deals';
    const isSourceShippedByYou =
      fulfillmentType === 'shipped' && whoShipsProduct === 'myCompany';

    const isTargetDigital =
      targetDeliveryMethod === SamplingDeliveryMethod.DigitalDelivery;
    const isTargetShippedByYou =
      targetDeliveryMethod === SamplingDeliveryMethod.Shipped;
    const isTargetShippedByBV =
      targetDeliveryMethod === SamplingDeliveryMethod.ShippedByBazaarvoice;

    interface TransitionRule {
      condition: boolean;
      removedSection?: SamplingCampaignRequestSectionName;
    }

    const transitionRules: TransitionRule[] = [
      {
        condition:
          isSourceShippedByBV && (isTargetDigital || isTargetShippedByYou),
        removedSection: SamplingCampaignRequestSectionName.PACKAGING,
      },
      {
        condition:
          isSourceDeals && (isTargetShippedByYou || isTargetShippedByBV),
        removedSection: SamplingCampaignRequestSectionName.FULFILLMENT,
      },
      {
        condition: isSourceShippedByYou && isTargetDigital,
        removedSection: SamplingCampaignRequestSectionName.FULFILLMENT,
      },
      {
        condition: isSourceShippedByYou && isTargetShippedByBV,
        removedSection: undefined,
      },
    ];

    const matchedRule = transitionRules.find(({ condition }) => condition);

    return {
      fulfillmentType,
      removedDataSection: matchedRule?.removedSection,
      shouldResetFulfillment: !!matchedRule,
      targetCampaignType: targetDeliveryMethod,
    };
  };

  const updateFormValuesForSetup = () => {
    const data = getValues();
    const currentSelectedOptions = (data.purchaseOption ?? []).filter(
      (opt) => !expiredSavedIds.has(opt.opportunityId),
    );
    setValue('purchaseOption', currentSelectedOptions);
    const savedOptions = defaultValues?.purchaseOption ?? [];

    const selectedIds = new Set(
      currentSelectedOptions.map((opt) => opt.opportunityId).filter(isDefined),
    );
    const savedIds = new Set(
      savedOptions.map((opt) => opt?.opportunityId).filter(isDefined),
    );
    const hasSelectionChanged =
      currentSelectedOptions.some(
        (opt) =>
          isDefined(opt.opportunityId) && !savedIds.has(opt.opportunityId),
      ) ||
      savedOptions.some(
        (opt) =>
          isDefined(opt?.opportunityId) && !selectedIds.has(opt.opportunityId),
      );

    if (currentSelectedOptions.length > 0) {
      setValue('isNewPurchase', false);
    }

    if (currentSelectedOptions.length > 0 && hasSelectionChanged) {
      const currentFulfillmentOptions = getValues('fulfillmentOptions') || {};
      const { shouldResetFulfillment } = getConfirmationContext();
      const baseOptions = shouldResetFulfillment
        ? {}
        : currentFulfillmentOptions;

      const hasDeliveryMethod = currentSelectedOptions.some(
        (opt) => opt.deliveryMethod,
      );
      if (hasDeliveryMethod) {
        setValue('fulfillmentOptions', {
          ...baseOptions,
          ...getFulfillmentOptionsFromPurchaseOptions(currentSelectedOptions),
        });
      }

      const hasContentType = currentSelectedOptions.some(
        (opt) => opt.contentType,
      );
      if (hasContentType) {
        const existingMemberTasks = getValues('memberTasks') ?? {};
        const existingDisplayReviews = getValues('displayReviews') ?? {};
        const { displayReviews, memberTasks } =
          getMemberTasksFromPurchaseOptions(currentSelectedOptions);
        setValue('memberTasks', {
          ...memberTasks,
          // Preserve existing social task details (handles, hashtags) if the
          // new purchase option still supports social content. Clear otherwise.
          socials: memberTasks.postOnSocial ? existingMemberTasks.socials : [],
        });
        if (!memberTasks.postOnSocial) {
          setValue(
            'products',
            (getValues('products') ?? []).map((product) => ({
              ...product,
              variations: product.variations?.map((variation) => ({
                ...variation,
                allocations: undefined,
              })),
            })),
          );
          setValue('guidance', []);
          setValue('contentExampleImageUrls', []);
          setValue('contentExampleVideoUrls', []);
        }
        // Preserve existing review display settings if the new purchase option
        // still supports reviews. Reset to empty otherwise.
        setValue(
          'displayReviews',
          memberTasks.writeReview ? existingDisplayReviews : displayReviews,
        );
      }
    }
  };

  const handleFormSubmit = async (data: SamplingCampaignRequest) => {
    try {
      setSubmissionError(null);
      resetMainForm(data);
      await handleSave(false);
      const campaignId = data.campaignRequestId;
      if (campaignId) {
        void navigateToState(
          'campaigns.sampling-prepaid-campaign-request-details-edit-products',
          {
            campaignId,
          },
        );
      }
      handleClose();
    } catch (e) {
      setSubmissionError(
        e instanceof Error ? e.message : USER_ERROR_MESSAGES.DEFAULT,
      );
    }
  };

  const handleNextOrSubmit = async () => {
    if (step === ChangePurchaseOptionModalStep.PURCHASE_OPTIONS) {
      if (selectedOptionIds.length > 0 && isCompatible) {
        setStep(ChangePurchaseOptionModalStep.CONFIRM);
      }
    } else if (step === ChangePurchaseOptionModalStep.CONFIRM) {
      updateFormValuesForSetup();
      if (
        isSamplingContentDestinationsEnabled &&
        selectedOptionIds.length > 0
      ) {
        if (
          activeSelectedOptions.every(
            (opt) => !isSingleRetailerPurchaseOption(opt),
          )
        ) {
          setValue('targetClientId', '');
        }
        setStep(ChangePurchaseOptionModalStep.LOCATION);
      } else {
        setStep(ChangePurchaseOptionModalStep.SETUP);
      }
    } else if (
      isSamplingContentDestinationsEnabled &&
      step === ChangePurchaseOptionModalStep.LOCATION
    ) {
      if (
        selectedOptionIds.length > 0 &&
        activeSelectedOptions.every((opt) =>
          isSingleRetailerPurchaseOption(opt),
        )
      ) {
        setStep(ChangePurchaseOptionModalStep.SINGLE_RETAILER_SELECTION);
      } else {
        await localFormMethods.handleSubmit(handleFormSubmit)();
      }
    } else if (
      step === ChangePurchaseOptionModalStep.SINGLE_RETAILER_SELECTION
    ) {
      const currentTargetClientId = getValues('targetClientId');
      const savedTargetClientId = defaultValues?.targetClientId;
      if (currentTargetClientId !== savedTargetClientId) {
        setValue('products', []);
      }
      await localFormMethods.handleSubmit(handleFormSubmit)();
    } else if (step === ChangePurchaseOptionModalStep.SETUP) {
      if (isSamplingContentDestinationsEnabled && isNewPurchase) {
        const isValid = await localFormMethods.trigger();
        if (isValid) {
          setStep(ChangePurchaseOptionModalStep.CONTENT_DESTINATIONS);
        }
      } else {
        await localFormMethods.handleSubmit(handleFormSubmit)();
      }
    } else {
      await localFormMethods.handleSubmit(handleFormSubmit)();
    }
  };

  const handleBack = () => {
    if (step === ChangePurchaseOptionModalStep.CONTENT_DESTINATIONS) {
      setStep(ChangePurchaseOptionModalStep.SETUP);
    } else if (
      step === ChangePurchaseOptionModalStep.SINGLE_RETAILER_SELECTION
    ) {
      setStep(ChangePurchaseOptionModalStep.LOCATION);
    } else if (step === ChangePurchaseOptionModalStep.LOCATION) {
      setStep(ChangePurchaseOptionModalStep.CONFIRM);
    } else if (step === ChangePurchaseOptionModalStep.SETUP) {
      if (isNewPurchase) {
        setValue(
          'isNewPurchase',
          mainFormMethods.getValues('isNewPurchase') ?? false,
        );
        setValue(
          'purchaseOption',
          mainFormMethods.getValues('purchaseOption') ?? [],
        );
        setStep(ChangePurchaseOptionModalStep.PURCHASE_OPTIONS);
      } else {
        setStep(ChangePurchaseOptionModalStep.CONFIRM);
      }
    } else if (step === ChangePurchaseOptionModalStep.CONFIRM) {
      setStep(ChangePurchaseOptionModalStep.PURCHASE_OPTIONS);
    } else {
      handleClose();
    }
  };

  const isPurchaseOptionsStep =
    step === ChangePurchaseOptionModalStep.PURCHASE_OPTIONS;
  const isConfirmStep = step === ChangePurchaseOptionModalStep.CONFIRM;
  const isLocationStep = step === ChangePurchaseOptionModalStep.LOCATION;
  const isSetupStep = step === ChangePurchaseOptionModalStep.SETUP;

  const isNewPurchaseSteps =
    isNewPurchase && (isSetupStep || isContentDestinationsStep);
  const isUpdateDisabled =
    !isNewPurchaseSteps && (selectedOptionIds.length === 0 || !isCompatible);

  const hasExpiredSelectedOption = savedPurchaseOptionsValues?.some((saved) => {
    const latestOption = availableSavedOptions.find(
      (opp) => opp.opportunityId === saved.opportunityId,
    );
    const optionToCheck = latestOption ?? saved;

    return isPurchaseOptionExpiredOrZeroUnits(optionToCheck);
  });

  const expiredOriginalOptions = savedPurchaseOptionsValues
    ?.map(
      (option) =>
        availableSavedOptions.find(
          (original) => original.opportunityId === option.opportunityId,
        ) ?? option,
    )
    .filter(isPurchaseOptionExpiredOrZeroUnits);

  const derivedBaseModalTitle = (() => {
    if (hasExpiredSelectedOption) {
      return UPDATE_EXPIRED_CHANGE_PURCHASE_OPTION_MODAL_TITLE;
    }
    if (isLegacy) {
      return UPDATE_DRAFT_CAMPAIGN_MODAL_TITLE;
    }
    return CHANGE_PURCHASE_OPTION_MODAL_TITLE;
  })();

  const derivedHeaderText = (() => {
    if (hasExpiredSelectedOption) {
      return '';
    }
    if (isLegacy) {
      return UPDATE_DRAFT_CAMPAIGN_HEADER_TEXT;
    }
    return CHANGE_PURCHASE_OPTION_HEADER_TEXT;
  })();

  const currentModalTitle = (() => {
    if (isPurchaseOptionsStep) {
      return derivedBaseModalTitle;
    }
    if (isConfirmStep) {
      return 'Are You Sure?';
    }
    if (isContentDestinationsStep) {
      return 'Campaign Request | Content Destination';
    }
    return 'Update Campaign';
  })();

  const primaryButtonText = (() => {
    if (isPurchaseOptionsStep || isConfirmStep) {
      return 'Next';
    }
    if (isLocationStep) {
      const selectedOption = selectedOptions[0];
      if (selectedOption && isSingleRetailerPurchaseOption(selectedOption)) {
        return 'Next';
      }
    }
    if (isSetupStep && isSamplingContentDestinationsEnabled && isNewPurchase) {
      return 'Continue';
    }
    return 'Update Campaign';
  })();

  const tertiaryButtonInfo = (() => {
    if (hasExpiredSelectedOption) {
      return {
        buttonContent: 'Go back to Campaigns',
        onClick: handleGoBackToCampaigns,
        trackingId: SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_BACK_BUTTON,
      };
    }
    if (
      isSamplingContentDestinationsEnabled &&
      (isLocationStep ||
        isSingleRetailerSelectionStep ||
        isContentDestinationsStep)
    ) {
      return {
        buttonContent: 'Cancel',
        onClick: handleClose,
        trackingId: SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_BACK_BUTTON,
      };
    }
    return undefined;
  })();

  const modalWidth = (() => {
    if (isPurchaseOptionsStep) {
      return 'extra-wide';
    }
    if (
      isLocationStep ||
      isSingleRetailerSelectionStep ||
      isContentDestinationsStep
    ) {
      return 'wide';
    }
    return 'medium';
  })();

  const renderContent = () => {
    switch (step) {
      case ChangePurchaseOptionModalStep.PURCHASE_OPTIONS: {
        if (isCheckingExpired || isLoading) {
          return <CenteredSpinner />;
        }
        return (
          <>
            {hasExpiredSelectedOption && (
              <ExpiredPurchaseOptionsTable
                expiredSelectedOptions={expiredOriginalOptions ?? []}
              />
            )}
            <ChangePurchaseOptionModalTable
              allSelectedOptions={activeSelectedOptions}
              currentPage={currentPage}
              error={error}
              expiredPurchaseOptionModalTitle={
                hasExpiredSelectedOption
                  ? CHANGE_PURCHASE_OPTION_MODAL_TITLE
                  : undefined
              }
              headerText={derivedHeaderText}
              isLoading={isPending}
              itemsPerPage={itemsPerPage}
              onCreateNewPurchase={handleCreateNewPurchase}
              onSelectionChange={handleSelectionChange}
              onSortChange={handleSortChange}
              purchaseOptions={purchaseOptions}
              requestContext={{
                campaignName: samplingCampaignRequest?.name,
                requestShortCode: samplingCampaignRequest?.externalId,
              }}
              resultsText={resultsText}
              searchTerm={searchTerm}
              selectedCount={selectedOptionIds.length}
              selectedOptionIds={selectedOptionIds}
              setCurrentPage={handlePageChange}
              setItemsPerPage={handleItemsPerPageChange}
              setSearchTerm={handleSearchChange}
              showCreateNewPurchaseButton={
                !samplingCampaignRequest?.isNewPurchase
              }
              sortDir={sortDir}
              sortKey={sortKey}
              totalItems={totalItems}
            />
          </>
        );
      }
      case ChangePurchaseOptionModalStep.CONFIRM: {
        const { fulfillmentType, removedDataSection, targetCampaignType } =
          getConfirmationContext();
        return (
          <ChangePurchaseOptionConfirmation
            fulfillmentType={fulfillmentType}
            removedDataSection={removedDataSection}
            targetCampaignType={targetCampaignType}
          />
        );
      }
      case ChangePurchaseOptionModalStep.LOCATION:
        return (
          <PrepaidLocationModal
            deliveryMethod={getConfirmationContext().targetCampaignType}
          />
        );
      case ChangePurchaseOptionModalStep.SINGLE_RETAILER_SELECTION:
        return <PrepaidRetailerSelectionModal />;
      case ChangePurchaseOptionModalStep.SETUP:
        return <CampaignModalContentSetup />;
      case ChangePurchaseOptionModalStep.CONTENT_DESTINATIONS:
        return <ContentDestinationsStep />;
      default:
        throw new Error('Invalid modal step');
    }
  };
  return (
    <ChangePurchaseOptionModalProvider
      value={{
        formMethods: localFormMethods,
        handleFormSubmit,
        isPending: isSaving,
        setStep,
        step,
        submissionError,
      }}
    >
      <FormProvider {...localFormMethods}>
        <Modal
          closeModal={
            isDefined(samplingCampaignRequest?.isNewPurchase) &&
            !hasExpiredSelectedOption
              ? handleClose
              : undefined
          }
          contents={renderContent()}
          isModalOpen={isModalOpen}
          primaryButtonInfo={{
            buttonContent: primaryButtonText,
            isDisabled: isUpdateDisabled || isSaving || isLoading,
            onClick: handleNextOrSubmit,
            trackingId:
              SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_PURCHASE_OPTIONS_CHANGE_BUTTON,
          }}
          secondaryButtonInfo={
            !isPurchaseOptionsStep
              ? {
                  buttonContent: 'Back',
                  onClick: handleBack,
                  trackingId:
                    SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_PURCHASE_OPTIONS_CHANGE_CANCEL_BUTTON,
                }
              : undefined
          }
          shouldCloseOnClickOutside={false}
          shouldCloseOnEsc={false}
          shouldIncludeSecondaryButton={!isPurchaseOptionsStep}
          shouldIncludeTertiaryButton={
            shouldIncludeTertiaryButton ||
            hasExpiredSelectedOption ||
            isLocationStep ||
            isSingleRetailerSelectionStep ||
            isContentDestinationsStep
          }
          tertiaryButtonInfo={tertiaryButtonInfo}
          title={currentModalTitle}
          width={modalWidth}
        />
      </FormProvider>
    </ChangePurchaseOptionModalProvider>
  );
};
